import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'
import api from '@dashboard/services/axiosApi'
import { useConfigStore } from '@dashboard/stores/configStore'
import type { Config } from '@dashboard/types/config'
import { audienceDisplayName, buildGroupCatalog } from '../services/editor/groups'

/** A measurement that publishing the stored draft restarts, from `App\Services\Experiments\PublishImpact`. */
export type MeasurementRestart = {
  kind: 'holdback' | 'original_test' | 'audience_test'
  experimentId: string
  campaignId: string
  campaignName: string
  audienceId: string | null
  audienceName: string | null
  startedAt: string
  weeks: number
  enrolled: number | null
}

export type NamedRestart = MeasurementRestart & { name: string; detail: string }

function restartName(row: MeasurementRestart, config: Config | null): string {
  if (row.kind === 'holdback') {
    return 'Campaign vs holdback'
  }
  if (row.kind === 'original_test') {
    return 'Original page A/B test'
  }
  const audience = config?.campaigns
    .find((campaign) => campaign.id === row.campaignId)
    ?.variants.find((variant) => variant.id === row.audienceId)
  const name = audience && config ? audienceDisplayName(audience, buildGroupCatalog(config)) : row.audienceName
  return `${name ?? 'Audience'} A/B test`
}

export function nameRestarts(rows: readonly MeasurementRestart[], config: Config | null): NamedRestart[] {
  return rows.map((row) => {
    const age = row.weeks < 1 ? 'less than a week' : `${row.weeks} ${row.weeks === 1 ? 'week' : 'weeks'}`
    const visitors =
      row.enrolled === null
        ? ''
        : `, ${row.enrolled.toLocaleString('en-US')} ${row.enrolled === 1 ? 'visitor' : 'visitors'}`
    return { ...row, name: restartName(row, config), detail: `${age}${visitors}` }
  })
}

/** "Publishing restarts measurement for A (4 weeks, 46 visitors) and B (2 weeks)." */
export function restartSentence(rows: readonly NamedRestart[]): string {
  const items = rows.map((row) => `${row.name} (${row.detail})`)
  const list = items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items.at(-1)}` : (items[0] ?? '')
  return `Publishing restarts measurement for ${list}.`
}

/** A week of data is worth an explicit acknowledgement before it starts over. */
export function restartNeedsAcknowledgement(rows: readonly MeasurementRestart[]): boolean {
  return rows.some((row) => row.weeks >= 1)
}

/** Measurements the stored draft restarts on publish, refetched whenever the stored draft changes. */
export function usePublishImpact() {
  const configStore = useConfigStore()
  const hasDraft = computed(() => Boolean(configStore.fullResponse?.draft))
  const query = useQuery({
    queryKey: computed(() => ['publish-impact', configStore.currentTeamPid, configStore.lastChecksum, hasDraft.value]),
    queryFn: async ({ signal }) => {
      const { data } = await api.get<{ restarts: MeasurementRestart[] }>(
        `/api/${configStore.currentTeamPid}/metrics/publish-impact`,
        { signal, skipProgress: true }
      )
      return data.restarts
    },
    enabled: computed(() => Boolean(configStore.currentTeamPid) && hasDraft.value),
    retry: false,
    staleTime: 60_000
  })
  const restarts = computed(() =>
    hasDraft.value ? nameRestarts(query.data.value ?? [], configStore.config ?? null) : []
  )
  return { restarts, loading: query.isLoading }
}
